import { useState, useEffect } from 'react';
import { toaster } from 'evergreen-ui';
import { fetchTurniByMonth, saveTurno } from '../../services/turniService';
import { useAuth } from '../../services/authContext';
import { fetchCollaborators } from '../../services/managementServices';

const useTurni = ({ isDirector, isEducator }) => {
  const { user } = useAuth();
  const userName = user ? `${user.nome || ''} ${user.cognome || ''}`.trim() : 'Operatore';

  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [turniData, setTurniData] = useState([]);
  const [collaboratorsList, setCollaboratorsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [shiftInputs, setShiftInputs] = useState({
    date: '',
    shift_type: 'turno',
    start_time: '',
    end_time: '',
    operator_name: userName,
    notes: '',
    is_overtime: false,
  });

  useEffect(() => {
    if (isDirector || isEducator) {
      fetchCollaborators()
        .then(data => {
          if (Array.isArray(data)) setCollaboratorsList(data);
        })
        .catch(err => console.error('Errore caricamento collaboratori', err));
    }
  }, [isDirector, isEducator]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchTurniByMonth(selectedMonth);
      setTurniData(data || []);
    } catch (e) {
      toaster.danger('Errore durante il caricamento turni: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const handleSaveTurno = async () => {
    try {
      const { start_time, end_time, date } = shiftInputs;

      if (start_time && end_time && start_time > end_time) {
        const firstShift = { ...shiftInputs, end_time: '23:59' };
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        const nextDayString = nextDay.toISOString().split('T')[0];
        const secondShift = { ...shiftInputs, date: nextDayString, start_time: '00:00' };
        await saveTurno(firstShift);
        await saveTurno(secondShift);
        toaster.success('Turno salvato su due giornate consecutive');
      } else {
        await saveTurno(shiftInputs);
        toaster.success('Turno salvato con successo');
      }

      setShiftInputs({ date: '', shift_type: 'turno', start_time: '', end_time: '', operator_name: userName, notes: '', is_overtime: false });
      loadData();
      return true;
    } catch (e) {
      toaster.danger('Errore: ' + e.message);
      return false;
    }
  };

  const handleDeleteTurno = async (id) => {
    try {
      const { deleteTurno } = await import('../../services/turniService');
      await deleteTurno(id);
      toaster.success('Turno rimosso');
      loadData();
    } catch (e) {
      toaster.danger('Errore rimozione: ' + e.message);
      throw e;
    }
  };

  const handleTimbra = async (shiftId, shiftDate) => {
    if (shiftDate > new Date().toISOString().split('T')[0]) {
      toaster.warning('Non è possibile timbrare un turno futuro');
      return;
    }
    try {
      const { timbraTurno } = await import('../../services/turniService');
      await timbraTurno(shiftId);
      toaster.success('Turno timbrato!');
      loadData();
    } catch (e) {
      toaster.danger('Errore timbratura: ' + e.message);
    }
  };

  const handleDragStart = (e, userNameStr) => {
    e.dataTransfer.setData('userName', userNameStr);
  };

  const handleDrop = (e, dateStr) => {
    e.preventDefault();
    if (!isDirector) return;
    const droppedName = e.dataTransfer.getData('userName');
    if (droppedName) {
      setShiftInputs(prev => ({ ...prev, operator_name: droppedName, date: dateStr, shift_type: 'turno' }));
      return droppedName;
    }
    return null;
  };

  return {
    userName,
    selectedMonth,
    setSelectedMonth,
    turniData,
    collaboratorsList,
    loading,
    shiftInputs,
    setShiftInputs,
    loadData,
    handleSaveTurno,
    handleDeleteTurno,
    handleTimbra,
    handleDragStart,
    handleDrop,
  };
};

export default useTurni;
